import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from './Reports.redux';
import { useNavigate } from '@tanstack/react-router';
import api from 'api';
import saveAs from 'file-saver';
import { actions as fileActions } from 'components/FileManager/FileManager.redux';
import FileManager from 'components/FileManager';
import wysiwygSx from './WysiwygEditor.styles';
import SidebarTemplates from './SidebarTemplates';
import { useTheme } from '@mui/material/styles';
import { Box, Drawer, IconButton, Typography, useMediaQuery } from '@mui/material';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';

const defaultFileManagerProps = {
    client_id: null,
    patient_id: null,
    visit_id: null,
    urpt_id: null,
    defaultPage: 'upload',
};

const saveSVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" focusable="false" aria-hidden="true"><path fill="currentColor" d="M17 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V7zm-5 16c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3m3-10H5V5h10z"/></svg>`;
const pdfSVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" focusable="false" aria-hidden="true"><path fill="currentColor" d="M20 2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2m-8.5 7.5c0 .83-.67 1.5-1.5 1.5H9v2H7.5V7H10c.83 0 1.5.67 1.5 1.5zm5 2c0 .83-.67 1.5-1.5 1.5h-2.5V7H15c.83 0 1.5.67 1.5 1.5zm4-3H19v1h1.5V11H19v2h-1.5V7h3zM9 9.5h1v-1H9zM4 6H2v14c0 1.1.9 2 2 2h14v-2H4zm10 5.5h1v-3h-1z"></path></svg>`;

const WysiwygEditor = ({
    value = '',
    onChange,
    templates = [],
    showReplacementButton = false,
    navItems = [],
    autofill = [],
    fileManagerProps = { ...defaultFileManagerProps },
    handleSave,
    disableSave = false,
    showDownloadPDF = false,
}) => {
    const editorRef = useRef(null),
        dispatch = useDispatch(),
        navigate = useNavigate(),
        reportName = useSelector((state) => state.reports.report?.urpt_name),
        [mceEditor, setMceEditor] = useState(null),
        [loadingPdf, setLoadingPdf] = useState(false),
        [isDrawerExpanded, setIsDrawerExpanded] = useState(true);

    const theme = useTheme();
    const isXlUp = useMediaQuery(theme.breakpoints.up('xl'));
    const drawerWidth = isDrawerExpanded ? 360 : 72;
    const sx = wysiwygSx(theme, { drawerWidth });

    const getPDF = useCallback(async () => {
        setLoadingPdf(true);

        try {
            let pdf = await api.post('/pdf', { content: editorRef.current.getContent() }, { timeout: 15000 });

            let binary = atob(pdf.data.replace(/\s/g, '')),
                len = binary.length,
                buffer = new ArrayBuffer(len),
                view = new Uint8Array(buffer);

            for (let i = 0; i < len; i++) {
                view[i] = binary.charCodeAt(i);
            }

            let blob = new Blob([view], { type: 'application/pdf' }),
                d = new Date(),
                fileName = `Sabertooth Report - ${reportName} - ${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}.pdf`;

            saveAs(blob, fileName);
        } catch (err) {
            console.error(err);
        }

        setLoadingPdf(false);
    }, [reportName]);

    const handleOpenFileManager = () => {
        dispatch(fileActions.setState({ open: true }));
    };

    const refs = {
        handleSave: useRef(handleSave),
        getPDF: useRef(getPDF),
        loadingPdf: useRef(loadingPdf),
        toolbarPdfButtonApi: useRef(null),
        toolbarSaveButtonApi: useRef(null),
        menuPdfButtonApi: useRef(null),
        menuSaveButtonApi: useRef(null),
    };

    useEffect(() => {
        refs.handleSave.current = handleSave;
    }, [handleSave]);

    useEffect(() => {
        refs.getPDF.current = getPDF;
    }, [getPDF]);

    useEffect(() => {
        refs.toolbarSaveButtonApi.current?.setDisabled?.(disableSave);
        refs.menuSaveButtonApi.current?.setDisabled?.(disableSave);
    }, [disableSave]);

    useEffect(() => {
        refs.loadingPdf.current = loadingPdf;
        refs.toolbarPdfButtonApi.current?.setDisabled?.(loadingPdf);
        refs.menuPdfButtonApi.current?.setDisabled?.(loadingPdf);
    }, [loadingPdf]);

    const { stToolbar, stPlugins, stMenu } = useMemo(() => {
        if (showDownloadPDF) {
            return {
                stPlugins: 'stFileManager stReplaceTemplates stSave stPDF',
                stToolbar: 'stSave stPDF',
                stMenu: 'stSaveMenu stPDFMenu',
            };
        }

        return {
            stPlugins: 'stFileManager stReplaceTemplates stSave',
            stToolbar: 'stSave',
            stMenu: 'stSaveMenu',
        };
    }, [showDownloadPDF]);

    return (
        <Box sx={sx.container}>
            <Box sx={sx.editorWrapper}>
                <Editor
                    tinymceScriptSrc="/lib/tinymce/tinymce.min.js"
                    value={value}
                    onEditorChange={onChange}
                    onInit={(event, editor) => {
                        editorRef.current = editor;

                        let onAction = function (autocompleteApi, range, value) {
                            editor.selection.setRng(range);
                            editor.insertContent(value);
                            autocompleteApi.hide();
                        };

                        let getMatchedChars = function (pattern) {
                            return autofill.filter(function (char) {
                                return char.text.toLowerCase().startsWith(pattern);
                            });
                        };

                        // Start autocomplete
                        editor.ui.registry.addAutocompleter('specialchars_cardmenuitems', {
                            ch: '/',
                            minChars: 0,
                            columns: 1,
                            highlightOn: ['char_name'],
                            onAction: onAction,
                            fetch: function (pattern) {
                                return new Promise(function (resolve) {
                                    let results = getMatchedChars(pattern).map(function (char) {
                                        return {
                                            type: 'cardmenuitem',
                                            value: char.value,
                                            label: char.text,
                                            items: [
                                                {
                                                    type: 'cardcontainer',
                                                    direction: 'vertical',
                                                    items: [
                                                        {
                                                            type: 'cardtext',
                                                            text: char.text,
                                                            name: 'char_name',
                                                        },
                                                    ],
                                                },
                                            ],
                                        };
                                    });
                                    resolve(results);
                                });
                            },
                        });
                        // End autocomplete
                    }}
                    init={{
                        branding: false,
                        height: '100%',
                        width: '100%',
                        resize: false,
                        browser_spellcheck: true,
                        menu: {
                            navigation: {
                                title: 'Navigate',
                                items: navItems.map((navItem) => navItem.key).join(' '),
                            },
                            file: { title: 'File', items: `${stMenu} | print preview` },
                        },
                        menubar: `file edit format table insert ${navItems.length ? 'navigation' : ''}`,
                        pagebreak_separator: '<div class="page-break st-page-break"></div>',
                        plugins: [
                            'link code table lists hr paste print template pagebreak',
                            'advlist autolink lists charmap print preview anchor',
                            'searchreplace visualblocks code fullscreen',
                            'insertdatetime table paste code help wordcount',
                            stPlugins,
                        ],
                        toolbar:
                            stToolbar +
                            ' | undo redo | cut copy paste | ' +
                            'formatselect fontselect fontsizeselect | ' +
                            'bold italic underline forecolor backcolor removeformat | ' +
                            'alignleft aligncenter alignright alignjustify | ' +
                            'numlist bullist outdent indent pagebreak | ' +
                            'table hr stFileManager template stReplaceTemplates | ' +
                            'help',
                        content_style: `body { font-family:Helvetica,Arial,sans-serif; font-size:14px }

                    /* custom page break separator */
                    .st-page-break {
                        position: relative;
                        margin: 20px 0;
                        border-top: 3px dashed #d32f2f;
                        height: 0;
                    }

                    /* fallback for TinyMCE default page break marker */
                    img.mce-pagebreak,
                    hr.mce-pagebreak {
                        border: 0;
                        border-top: 3px dashed #d32f2f !important;
                        margin: 20px 0 !important;
                    }

                    @media print {
                        .st-page-break {
                            border-top: 0 !important;
                            margin: 0 !important;
                            height: 0 !important;
                        }
                        .st-page-break > span {
                            display: none !important;
                        }

                        /* fallback markers */
                        img.mce-pagebreak,
                        hr.mce-pagebreak {
                            border: 0 !important;
                            margin: 0 !important;
                        }
                    }`,
                        fontsize_formats: '8pt 10pt 11pt 12pt 13pt 14pt 16pt 18pt 24pt 36pt',
                        templates: templates,
                        toolbar_mode: 'wrap',
                        setup: (editor) => {
                            setMceEditor(editor);

                            navItems.forEach((navItem) => {
                                editor.ui.registry.addMenuItem(navItem.key, {
                                    text: navItem.label,
                                    onAction: function () {
                                        if (navItem.newTab) {
                                            window.open(navItem.location, '_blank').focus();
                                        } else {
                                            navigate({ to: navItem.location });
                                        }
                                    },
                                });
                            });

                            // Add custom icons for the toolbar buttons.
                            editor.ui.registry.addIcon('stSaveIcon', saveSVG);
                            editor.ui.registry.addIcon('stPDFIcon', pdfSVG);

                            // Add a custom save button to the toolbar.
                            editor.ui.registry.addButton('stSave', {
                                icon: 'stSaveIcon',
                                tooltip: 'Save',
                                onAction: (api) => {
                                    if (!api.isDisabled()) {
                                        refs.handleSave.current?.();
                                    }
                                },
                                onSetup: (api) => {
                                    refs.toolbarSaveButtonApi.current = api;

                                    return () => {
                                        if (refs.toolbarSaveButtonApi.current === api) {
                                            refs.toolbarSaveButtonApi.current = null;
                                        }
                                    };
                                },
                            });

                            // Trigger custom save action from keyboard shortcut.
                            const triggerSave = () => {
                                const isToolbarDisabled = refs.toolbarSaveButtonApi.current?.isDisabled?.();
                                const isMenuDisabled = refs.menuSaveButtonApi.current?.isDisabled?.();

                                if (!isToolbarDisabled && !isMenuDisabled) {
                                    refs.handleSave.current?.();
                                }
                            };

                            // Add a keyboard shortcut for saving the document.
                            // TinyMCE maps `meta` appropriately across platforms (eg, ctrl/cmd)
                            editor.addShortcut('meta+s', 'Save', triggerSave);

                            // Add a custom save menu item.
                            editor.ui.registry.addMenuItem('stSaveMenu', {
                                text: 'Save',
                                icon: 'stSaveIcon',
                                onAction: () => {
                                    refs.handleSave.current?.();
                                },
                                onSetup: (api) => {
                                    refs.menuSaveButtonApi.current = api;

                                    return () => {
                                        if (refs.menuSaveButtonApi.current === api) {
                                            refs.menuSaveButtonApi.current = null;
                                        }
                                    };
                                },
                            });

                            // Add a custom PDF menu item.
                            editor.ui.registry.addMenuItem('stPDFMenu', {
                                icon: 'stPDFIcon',
                                text: 'Download PDF',
                                onAction: (api) => {
                                    if (!api.isDisabled()) {
                                        refs.getPDF.current();
                                    }
                                },
                                onSetup: (api) => {
                                    refs.menuPdfButtonApi.current = api;
                                    api.setDisabled(!!refs.loadingPdf.current);

                                    return () => {
                                        if (refs.menuPdfButtonApi.current === api) {
                                            refs.menuPdfButtonApi.current = null;
                                        }
                                    };
                                },
                            });

                            // Add a custom PDF button to the toolbar.
                            editor.ui.registry.addButton('stPDF', {
                                icon: 'stPDFIcon',
                                tooltip: 'Download PDF',
                                onAction: (api) => {
                                    if (!refs.loadingPdf.current && !api.isDisabled()) {
                                        refs.getPDF.current();
                                    }
                                },
                                onSetup: (api) => {
                                    refs.toolbarPdfButtonApi.current = api;
                                    api.setDisabled(!!refs.loadingPdf.current);

                                    return () => {
                                        if (refs.toolbarPdfButtonApi.current === api) {
                                            refs.toolbarPdfButtonApi.current = null;
                                        }
                                    };
                                },
                            });

                            // Add a custom file manager button to the toolbar.
                            editor.ui.registry.addButton('stFileManager', {
                                //text: "Image",
                                icon: 'image',
                                tooltip: 'Upload or Insert Image',
                                onAction: handleOpenFileManager,
                            });

                            if (showReplacementButton === true) {
                                editor.ui.registry.addButton('stReplaceTemplates', {
                                    text: 'Update',
                                    //icon: 'image',
                                    tooltip: 'Update Template Data',
                                    onAction: () => dispatch(actions.openTemplateReplacementModal()),
                                });
                            }
                        },
                    }}
                />
            </Box>

            {isXlUp && (
                <Drawer
                    anchor="right"
                    variant="permanent"
                    open
                    sx={sx.drawer}
                    PaperProps={{
                        onClick: !isDrawerExpanded ? () => setIsDrawerExpanded(true) : undefined,
                        sx: !isDrawerExpanded ? { cursor: 'pointer' } : undefined,
                    }}
                >
                    <Box sx={sx.drawerHeader}>
                        <IconButton
                            aria-label={isDrawerExpanded ? 'Collapse templates drawer' : 'Expand templates drawer'}
                            onClick={() => setIsDrawerExpanded((prev) => !prev)}
                        >
                            {isDrawerExpanded ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
                        </IconButton>

                        {isDrawerExpanded && <Typography variant="subtitle2">Templates</Typography>}
                    </Box>

                    {isDrawerExpanded && (
                        <Box sx={sx.drawerContent}>
                            <SidebarTemplates templates={templates} editor={mceEditor} />
                        </Box>
                    )}

                    {!isDrawerExpanded && (
                        <Box sx={sx.drawerCollapsedContent}>
                            <Typography variant="caption" sx={sx.verticalTemplatesText}>
                                {'TEMPLATES'.split('').map((letter, index) => (
                                    <Box component="span" sx={sx.templateLetter} key={`template-letter-${index}`}>
                                        {letter}
                                    </Box>
                                ))}
                            </Typography>
                        </Box>
                    )}
                </Drawer>
            )}

            <FileManager
                {...defaultFileManagerProps}
                {...fileManagerProps}
                selectActions={[
                    {
                        name: 'addToReport',
                        label: 'Add to report',
                        handler: (file) => {
                            if (mceEditor) {
                                mceEditor.execCommand(
                                    'mceInsertContent',
                                    false,
                                    `<img src=${file.file_url} style="display:block;max-width:100%;width:auto;height:auto;"/>`
                                );
                            }
                        },
                    },
                    {
                        name: 'addToReportAndClose',
                        label: 'Add to report and close',
                        handler: (file) => {
                            if (mceEditor) {
                                mceEditor.execCommand(
                                    'mceInsertContent',
                                    false,
                                    `<img src=${file.file_url} style="display:block;max-width:100%;width:auto;height:auto;"/>`
                                );
                                dispatch(fileActions.setState({ open: false }));
                            }
                        },
                    },
                ]}
            />
        </Box>
    );
};

export default WysiwygEditor;
